import { IncidentVideoQuery } from '@/graphql/generated/operations'
import { ClipKind } from '@/graphql/generated/schemas'

/**
 * `DOWNSCALED` is not in the generated `ClipKind` enum yet (backend hasn't introduced it —
 * ENG-30186 #4). Declared as its raw wire value so the resolution fallback already works the day
 * the backend starts returning it; replace with `ClipKind.Downscaled` once codegen picks it up.
 */
export const DOWNSCALED_CLIP_KIND = 'DOWNSCALED' as ClipKind

/**
 * Resolution order for the clip played in the incident drawer, mirroring api-general's
 * resolution order (ENG-30186 #5): ORIGINAL first, then DOWNSCALED. EXTENDED (forensic) clips
 * are intentionally excluded — the incident player only surfaces the resolved clip.
 */
export const RESOLVED_CLIP_KIND_ORDER: ClipKind[] = [
  ClipKind.Original,
  DOWNSCALED_CLIP_KIND,
]

type IncidentClips = NonNullable<IncidentVideoQuery['incident']>['clips']
type IncidentClipNode = NonNullable<
  NonNullable<NonNullable<IncidentClips>['edges']>[number]
>['node']

/**
 * Picks the clip to play from `Incident.clips` per {@link RESOLVED_CLIP_KIND_ORDER}, returning the
 * full clip node so callers get both the presigned `video` wrapper (`{ url, expiryTime }`, shape-
 * compatible with `Incident.video`) and the clip's wall-clock `startTime`/`endTime`. Only clips with
 * a playable `video.url` are considered.
 *
 * Returns `null` when no matching, playable clip is present — callers should fall back to
 * `Incident.video`.
 */
export const pickResolvedClip = (clips: IncidentClips): IncidentClipNode => {
  const nodes =
    clips?.edges
      ?.map((edge) => edge?.node)
      .filter((node): node is NonNullable<typeof node> => !!node?.video?.url) ??
    []

  for (const kind of RESOLVED_CLIP_KIND_ORDER) {
    const match = nodes.find((node) => node.kind === kind)
    if (match) {
      return match
    }
  }

  return null
}

/**
 * Returns the first EXTENDED (forensic/full-res) clip node, or `null` when the incident has no
 * playable EXTENDED clip. Used to offer the "Extended" toggle in the incident player and to surface
 * that clip's wall-clock range. An incident may have multiple EXTENDED clips (one per source
 * device); the first available one is returned.
 */
export const pickFirstExtendedClip = (
  clips: IncidentClips
): IncidentClipNode => {
  const match = clips?.edges
    ?.map((edge) => edge?.node)
    .find((node) => node?.kind === ClipKind.Extended && !!node?.video?.url)

  return match ?? null
}
